<template>
  <b-card class="gamification-settings">
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Gamification</h3>

      <b-form-group label="Enable Gamification to encourage your employees to compete against each other for points and prizes." label-cols-sm="8">
        <toggle class="m-0" style="width: 200px;" v-model="useGamification" @input="updateCompanyPreferences" label-true="Enable" label-false="Disable"/>
      </b-form-group>
      <hr>
      <b-form-group label="Enable the Reward Shop so employees can collect tokens to spend on prizes!" label-cols-sm="8">
        <toggle class="m-0" style="width: 200px;" v-model="isRewardsEnabled" @input="updateCompanyPreferences" label-true="Enable" label-false="Disable"/>
      </b-form-group>
      <b-form-group v-if="isRewardsEnabled" label-class="text-muted" label-cols-sm="8"
        label="Where should new Claimed Reward notifications go?">
          <content-editable class="mt-sm-1" type="EMAIL" placeholder="someone@example.com"
            v-model="recruiterClaimedRewardsEmail" @saved="updateCompanyPreferences"/>
      </b-form-group>
      <template v-if="useGamification">
        <hr>
        <div class="d-flex flex-column flex-fill w-100 pt-3">
          <h3>Leaderboards</h3>
          <p>Create and edit your leaderboards</p>
        </div>
        <div class="d-flex flex-column flex-fill w-100">
          <b-table striped hover :items="company.leaderboards" :fields="leaderboardFields" v-if="company.leaderboards.length">
            <template #cell(type)="{ value, item: { raffleMode } }">
              <template v-if="value === 'INDIVIDUALS'"><font-awesome-icon icon="user" style="margin-left:3px"/></template>
              <template v-if="value === 'DEPARTMENTS'"><font-awesome-icon icon="users"/></template>
              <template v-if="raffleMode">🎟</template>
              <template v-else>🏆</template>
            </template>

            <template #cell(startDate)="{ value }">
              <template v-if="value">{{ value | formatDate('DD/MMM') }}</template>
              <template v-else>∞</template>
            </template>
            <template #cell(endDate)="{ value }">
              <template v-if="value">{{ value | formatDate('DD/MMM') }}</template>
              <template v-else>∞</template>
            </template>

            <template #cell(status)="{ value }">
              <template v-if="value === 'ACTIVE'">Active</template>
              <template v-if="value === 'INACTIVE'">Inactive</template>
              <template v-if="value === 'DELETED'">Deleted</template>
            </template>

            <template #head(actions)>
              <b-button size="sm" @click="() => currentLeaderboard = {}" variant="outline-primary">Add</b-button>
            </template>
            <template #cell(actions)="{ item }">
              <b-dropdown split size="sm" @click="() => currentLeaderboard = item" variant="outline-primary">
                <template #button-content>Edit</template>
                <b-dropdown-item @click="() => duplicateLeaderboard(item)">Duplicate</b-dropdown-item>
                <b-dropdown-item @click="() => deleteLeaderboard(item)" variant="danger">Delete</b-dropdown-item>
              </b-dropdown>
            </template>

          </b-table>
          <div v-if="!company.leaderboards.length">
            <b-button @click="() => currentLeaderboard = {}" variant="outline-primary">
              Add your first leaderboard
            </b-button>
          </div>
        </div>
      </template>
    </template>

    <b-modal v-if="currentLeaderboard" visible size="lg" no-close-on-backdrop hide-footer
      @hide="() => currentLeaderboard = null"
      :title="currentLeaderboard.id ? `Edit Leaderboard: ${currentLeaderboard.name}` : 'Create Leaderboard'">
        <leaderboard-modal :value="currentLeaderboard" :segments="this.company.segments"
          @input="data => currentLeaderboard.id ? updateLeaderboard(data) : createLeaderboard(data)"/>
    </b-modal>
  </b-card>
</template>

<script>
import AssetDetailsFragment from '@/graphql/fragments/AssetDetails.gql';
import ContentEditable from '@src/components/misc/ContentEditable';
import gql from 'graphql-tag';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import LeaderboardModal, { pickProperties } from '@/components/settings/LeaderboardModal';
import Toggle from '@/components/misc/toggle';
import IconPicker from '@/components/misc/iconPicker';

const LeaderboardSettingsFragment = gql`
  ${AssetDetailsFragment}

  fragment LeaderboardSettingsDetails on Leaderboard {
    id
    name
    description
    type
    label
    startDate
    endDate
    raffleMode
    hidden

    membership {
      everyone
      departments {
        id
      }
      users {
        id
        fullName
      }
    }
    segments {
      id
      title
    }
    groupBy

    scores {
      id
      label
      icon
      description
      eventNames
      disabled
      maximum
      value
    }

    prizes {
      first {
        title
        description
        image {
          ...AssetDetails
        }
        url
        terms
      }
      second {
        title
        description
        image {
          ...AssetDetails
        }
        url
        terms
      }
      third {
        title
        description
        image {
          ...AssetDetails
        }
        url
        terms
      }
      text
    }

    created
    updated
    status
  }
`;

const UpdateCompanyPreferencesMutation = gql`
  mutation UpdateCompanyPreferences($preferences: CompanyPreferencesInput!) {
    updateCompany(update: { preferences: $preferences }) {
      id
      preferences {
        useGamification
        isRewardsEnabled
        recruiterClaimedRewardsEmail
      }
    }
  }
`;

const leaderboardCreateMutation = gql`
  ${LeaderboardSettingsFragment}

  mutation createLeaderboard($create: CreateLeaderboardInput!) {
    createLeaderboard(create: $create) {
      ...LeaderboardSettingsDetails
    }
  }
`;

const leaderboardUpdateMutation = gql`
  ${LeaderboardSettingsFragment}

  mutation updateLeaderboard($update: UpdateLeaderboardInput!, $leaderboardID: ID!) {
    updateLeaderboard(update: $update, where: { id: $leaderboardID }) {
      ...LeaderboardSettingsDetails
    }
  }
`;

// const leaderboardDeleteMutation = gql`
//   mutation deleteLeaderboard($leaderboardID: ID!) {
//     deleteLeaderboard(where: {id: $leaderboardID})
//   }
// `;

const GamificationQuery = gql`
  ${LeaderboardSettingsFragment}

  query GamificationPreferences {
    company {
      id
      name

      branding {
        smallLogo  {
          id
        }
      }

      leaderboards(where: { status_in: [ ACTIVE, INACTIVE ] }) {
        ...LeaderboardSettingsDetails
      }

      segments(where: { users: true }) {
        id
        title
      }

      preferences {
        useGamification
        isRewardsEnabled
        recruiterClaimedRewardsEmail
      }
    }
  }
`;

export default {
  name: 'GamificationSettings',
  components: {
    LoadingSpinner,
    ContentEditable,
    LeaderboardModal,
    Toggle,
    IconPicker,
  },
  data: () => ({
    company: null,
    err: null,
    useGamification: false,
    isRewardsEnabled: false,
    recruiterClaimedRewardsEmail: null,
    currentLeaderboard: null,
    form: {},
    formErrs: {
      scores: null,
    },
    formStates: {
      scores: null,
    },
    showIcons: null,
  }),
  metaInfo: {
    title: 'Gamification Settings',
  },
  apollo: {
    company: {
      query: GamificationQuery,
      result({ loading, data }) {
        if (!loading) {
          this.onFormReset();
        }
      },
      error(err) { this.err = `${err}`; },
    },
  },
  computed: {
    leaderboardFields: () => ([
      { key: 'name' },
      { key: 'type', sortable: true },
      { key: 'startDate' },
      { key: 'endDate' },
      { key: 'status', sortable: true },
      { key: 'actions' },
    ]),
  },
  methods: {
    onFormReset() {
      const { company } = this;
      this.useGamification = company.preferences.useGamification;
      this.isRewardsEnabled = company.preferences.isRewardsEnabled;
      this.recruiterClaimedRewardsEmail = company.preferences.recruiterClaimedRewardsEmail;
    },
    async updateCompanyPreferences() {
      const { useGamification, isRewardsEnabled, recruiterClaimedRewardsEmail } = this;
      const preferences = {
        useGamification,
        isRewardsEnabled,
        recruiterClaimedRewardsEmail: recruiterClaimedRewardsEmail || null,
      };
      this.$log.debug(preferences);

      await this.$apollo.mutate({
        mutation: UpdateCompanyPreferencesMutation,
        variables: { preferences },
        update(store, { data: { preferences } }) {
          const data = store.readQuery({ query: GamificationQuery });
          Object.assign(data.company.preferences, preferences);
          store.writeQuery({ query: GamificationQuery, data });
        },
      });
    },
    async createLeaderboard(create) {
      try {
        this.$log.debug(create);

        await this.$apollo.mutate({
          mutation: leaderboardCreateMutation,
          variables: { create },
          update(client, { data: { createLeaderboard: leaderboard } }) {
            const data = client.readQuery({ query: GamificationQuery });
            if (data.company && data.company.leaderboards) {
              data.company.leaderboards.push(leaderboard);
            }
            client.writeQuery({ query: GamificationQuery, data });
          },
        });

        this.currentLeaderboard = null;
      } catch (err) {
        if (err && Array.isArray(err.graphQLErrors) && err.graphQLErrors.length) {
          err.graphQLErrors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to create leaderboard:' }));
        } else if (err && err.networkError) {
          if (err.networkError && err.networkError.result && Array.isArray(err.networkError.result.errors) && err.networkError.result.errors.length) {
            err.networkError.result.errors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to create leaderboard:' }));
          } else {
            this.$notifyErr(err.networkError, { prepend: 'Failed to create leaderboard:' });
          }
        } else {
          this.$notifyErr(err, { prepend: 'Failed to create leaderboard:' });
        }
        this.currentLeaderboard = create;
      }
    },
    duplicateLeaderboard(original) {
      const { name, description, hidden, raffleMode, type, membership, prizes, scores } = original;
      const payload = pickProperties({
        name: `Copy of ${name}`,
        // eslint-disable-next-line object-property-newline
        description, hidden, raffleMode,
        status: 'INACTIVE',
        membership: { type, ...membership },
        prizes,
        scores,
      });
      return this.createLeaderboard(payload);
    },
    async updateLeaderboard(update) {
      try {
        const { id: leaderboardID } = this.currentLeaderboard;
        this.$log.debug({ leaderboardID }, update);

        await this.$apollo.mutate({
          mutation: leaderboardUpdateMutation,
          variables: { leaderboardID, update },
        });

        this.currentLeaderboard = null;
      } catch (err) {
        if (err && Array.isArray(err.graphQLErrors) && err.graphQLErrors.length) {
          err.graphQLErrors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to update leaderboard:' }));
        } else if (err && err.networkError) {
          if (err.networkError && err.networkError.result && Array.isArray(err.networkError.result.errors) && err.networkError.result.errors.length) {
            err.networkError.result.errors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to update leaderboard:' }));
          } else {
            this.$notifyErr(err.networkError, { prepend: 'Failed to update leaderboard:' });
          }
        } else {
          this.$notifyErr(err, { prepend: 'Failed to update leaderboard:' });
        }

        this.currentLeaderboard = {
          ...this.currentLeaderboard,
          ...update,
        };
      }
    },
    async deleteLeaderboard({ id: leaderboardID, name }) {
      if (!confirm(`Are you sure you want to delete the "${name}" leaderboard?`)) {
        return;
      }

      try {
        await this.$apollo.mutate({
          mutation: leaderboardUpdateMutation,
          variables: { leaderboardID, update: { status: 'DELETED' } },
          update(client) {
            const data = client.readQuery({ query: GamificationQuery });
            if (data.company && data.company.leaderboards) {
              const leaderboardIDs = data.company.leaderboards.map(({ id }) => id);
              data.company.leaderboards.splice(leaderboardIDs.indexOf(leaderboardID), 1);
            }
            client.writeQuery({ query: GamificationQuery, data });
          },
        });

        this.currentLeaderboard = null;
      } catch (err) {
        if (err && Array.isArray(err.graphQLErrors) && err.graphQLErrors.length) {
          err.graphQLErrors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to delete leaderboard:' }));
        } else if (err && err.networkError) {
          if (err.networkError && err.networkError.result && Array.isArray(err.networkError.result.errors) && err.networkError.result.errors.length) {
            err.networkError.result.errors.forEach(e => this.$notifyErr(e, { prepend: 'Failed to delete leaderboard:' }));
          } else {
            this.$notifyErr(err.networkError, { prepend: 'Failed to delete leaderboard:' });
          }
        } else {
          this.$notifyErr(err, { prepend: 'Failed to delete leaderboard:' });
        }
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/Gamification.vue